/**
 * Repeated-tool-call loop breaker.
 *
 * An agent can wedge a pod by calling one identical tool over and over —
 * observed live 2026-08-05, where a card polled a background build log
 * (`tail -20 …/web-build.log`) turn after turn while its own output said it
 * would wait for the completion notification instead of polling. The prompt
 * guidance against polling already existed; nothing bounded the repetition.
 *
 * `buildCanUseTool` (tool-access.ts) already sees every one of those calls —
 * build-capable spawns wire a `Bash` PreToolUse matcher, plan-permission spawns
 * wire a catch-all — but it only bounded DENIED calls. A run of ALLOWED
 * identical calls was invisible. This module supplies the missing count.
 *
 * ## Why deny, and why only every Nth repeat
 *
 * A PreToolUse deny is the only channel back into the model from the hook, so
 * a deny carrying an explanation is what actually breaks a repetition — and it
 * ends the tool call, not the turn, so the agent can immediately do something
 * else. But denying EVERY repeat would break a legitimate workflow: iterating
 * on a failing test re-runs the exact same command with edits in between, and
 * on a build spawn those edits are invisible here (only Bash is hooked). So the
 * interrupt fires on every `REPEAT_INTERRUPT_INTERVAL`-th repeat and the calls
 * between are allowed — a real workflow always makes progress, and a genuine
 * loop still gets interrupted.
 *
 * This is NOT the auto-mode stuck-nudge that `.claude/rules/agent-runner.md`
 * says must not come back. Nothing here re-prompts, re-queues, or wakes the
 * agent; it only interrupts a call the agent already repeated.
 */

/** Repeat count at which the loop is interrupted, and the interval after it. */
export const REPEAT_INTERRUPT_THRESHOLD = 4;
export const REPEAT_INTERRUPT_INTERVAL = 4;
/** Repeat count at which the session is force-stopped instead. */
export const REPEAT_FORCE_STOP_THRESHOLD = 12;

/**
 * Tools deliberately never counted. Both run Conveyor-owned gates with their
 * own retry semantics (ExitPlanMode is denied on purpose until the plan
 * validates, and AskUserQuestion is answered by a human), so a repeat of either
 * is expected rather than pathological.
 */
const UNCOUNTED_TOOLS = new Set(["ExitPlanMode", "AskUserQuestion"]);

export type LoopVerdict = "ok" | "interrupt" | "force_stop";

/** Stable JSON: object keys sorted so key order cannot mask a repeat. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`);
  return `{${entries.join(",")}}`;
}

/**
 * A comparable identity for one tool call, or null when the tool is not
 * counted. Bash is keyed on its `command` alone: `description`, `timeout` and
 * `run_in_background` differ between attempts that are otherwise the same
 * command, and re-issuing one command as a background job is still a repeat.
 */
export function fingerprintToolCall(
  toolName: string,
  input: Record<string, unknown>,
): string | null {
  if (!toolName || UNCOUNTED_TOOLS.has(toolName)) return null;
  if (toolName === "Bash") {
    const command = String(input.command ?? "")
      .trim()
      .replace(/\s+/g, " ");
    return command ? `Bash:${command}` : null;
  }
  return `${toolName}:${stableStringify(input)}`;
}

/**
 * Counts identical CONSECUTIVE tool calls. Lives on `QueryHost` (the
 * session-scoped QueryBridge) rather than in `buildCanUseTool`'s closure: that
 * closure is rebuilt by `buildQueryOptions` on every turn, so a loop spanning
 * turns — which is exactly what the reported card did — would reset its own
 * counter and never trip.
 */
export class ToolLoopTracker {
  private fingerprint: string | null = null;
  private streak = 0;
  /** True once this streak has been reported to chat — one message per streak. */
  private reported = false;

  /** Repeats of the current fingerprint, including the call being recorded. */
  get repeatCount(): number {
    return this.streak;
  }

  /** True when the current streak has already been posted to chat. */
  get alreadyReported(): boolean {
    return this.reported;
  }

  /** Mark the current streak as reported so the next interrupt stays quiet. */
  markReported(): void {
    this.reported = true;
  }

  /** Record one call and decide what to do about it. */
  record(fingerprint: string): LoopVerdict {
    if (fingerprint !== this.fingerprint) {
      this.fingerprint = fingerprint;
      this.streak = 1;
      this.reported = false;
      return "ok";
    }
    this.streak++;
    if (this.streak >= REPEAT_FORCE_STOP_THRESHOLD) return "force_stop";
    if (
      this.streak >= REPEAT_INTERRUPT_THRESHOLD &&
      (this.streak - REPEAT_INTERRUPT_THRESHOLD) % REPEAT_INTERRUPT_INTERVAL === 0
    ) {
      return "interrupt";
    }
    return "ok";
  }
}

/**
 * The deny text the model reads. It names the repeat count, because the model
 * cannot see its own loop, and branches on whether a singleton heavy gate is
 * running — a gate is the single most common thing an agent polls in a loop,
 * and the correct response to it is to end the turn.
 */
export function buildRepeatLoopMessage(repeatCount: number, heavyGateActive: boolean): string {
  const head =
    `Conveyor blocked this call: you have run the exact same command ${repeatCount} times in a row ` +
    `with nothing different in between. Repeating it again will return the same result.`;
  const advice = heavyGateActive
    ? `A build gate (test/typecheck/build) is running on this pod right now. Do NOT poll its log. ` +
      `End your turn — the completion notification re-invokes you when the gate finishes.`
    : `If you are waiting on a background job, end your turn instead of polling; the completion ` +
      `notification re-invokes you. If you are stuck, change your approach: read a different file, ` +
      `run a different command, or post to chat and ask the team.`;
  return `${head} ${advice} Call a different tool now.`;
}

/** The chat message a human reads when a loop is detected. */
export function buildRepeatLoopChatMessage(repeatCount: number, forceStopped: boolean): string {
  if (forceStopped) {
    return (
      `Agent force-stopped after repeating the same tool call ${repeatCount} times in a row. ` +
      `It appears stuck — send a message to resume.`
    );
  }
  return (
    `⚠️ The agent repeated the same tool call ${repeatCount} times in a row. ` +
    `Conveyor blocked the repeat and asked it to change approach.`
  );
}
